import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MenuPermissionService } from './menu-permission.service';

/** หน้าที่เปิดได้จริง (leaf) จากเมนูของผู้ใช้ — ใช้ค้นหา / เปิดล่าสุด / Ctrl+K */
export interface MenuLeaf {
  id: number;
  name: string;
  url: string;
  icon: string;
  heading: string;
  parentName: string;
  live?: boolean;
  badge?: { text: string; color: string };
  searchText: string;
}

const RECENT_STORAGE_KEY = 'sidebar_recent_menu_urls';
const RECENT_MAX = 6;

@Injectable({
  providedIn: 'root',
})
export class MenuNavigatorService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly permissionService = inject(MenuPermissionService);

  readonly paletteOpen = signal(false);
  readonly favoriteIds = signal<number[]>([]);
  readonly recentUrls = signal<string[]>([]);

  readonly leaves = computed<MenuLeaf[]>(() => {
    const list: MenuLeaf[] = [];
    for (const group of this.permissionService.menuGroups()) {
      for (const child of group.children) {
        if (child.submenu?.length) {
          for (const sub of child.submenu) {
            const url = sub.url || sub.path || '';
            if (!url) continue;
            list.push(this.toLeaf(sub.id, sub.name, url, child.icon || 'circle', group.heading, child.name, sub.live, sub.badge));
          }
        } else {
          const url = child.url || child.path || '';
          if (!url) continue;
          list.push(this.toLeaf(child.id, child.name, url, child.icon || 'circle', group.heading, '', child.live, child.badge));
        }
      }
    }
    return list;
  });

  readonly recentLeaves = computed<MenuLeaf[]>(() => {
    const byUrl = new Map(this.leaves().map((l) => [l.url, l]));
    return this.recentUrls()
      .map((u) => byUrl.get(u))
      .filter((l): l is MenuLeaf => !!l);
  });

  readonly favoriteLeaves = computed<MenuLeaf[]>(() => {
    const byId = new Map(this.leaves().map((l) => [l.id, l]));
    return this.favoriteIds()
      .map((id) => byId.get(id))
      .filter((l): l is MenuLeaf => !!l);
  });

  // ================= Normal Functions =================

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const stored = JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY) || '[]');
        this.recentUrls.set(Array.isArray(stored) ? stored : []);
      } catch {
        this.recentUrls.set([]);
      }
    }
  }

  /** ค้นหาทั้งชื่อไทย หมวด เมนูแม่ และคำอังกฤษจาก URL — ทุกคำต้องเจอ */
  search(query: string, limit = 50): MenuLeaf[] {
    const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    return this.leaves()
      .filter((l) => terms.every((t) => l.searchText.includes(t)))
      .slice(0, limit);
  }

  /** เรียกหลัง NavigationEnd — บันทึกเฉพาะ URL ที่อยู่ในเมนูของผู้ใช้ */
  trackVisit(currentUrl: string): void {
    const path = (currentUrl || '').split('?')[0].split('#')[0];
    const leaf = this.leaves().find((l) => path === l.url || path.startsWith(l.url + '/'));
    if (!leaf) return;
    const next = [leaf.url, ...this.recentUrls().filter((u) => u !== leaf.url)].slice(0, RECENT_MAX);
    this.recentUrls.set(next);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
    }
  }

  openPalette(): void {
    this.paletteOpen.set(true);
  }

  closePalette(): void {
    this.paletteOpen.set(false);
  }

  togglePalette(): void {
    this.paletteOpen.update((v) => !v);
  }

  private toLeaf(
    id: number,
    name: string,
    url: string,
    icon: string,
    heading: string,
    parentName: string,
    live?: boolean,
    badge?: { text: string; color: string },
  ): MenuLeaf {
    const urlWords = url.replace(/[/_-]+/g, ' ');
    return {
      id,
      name,
      url,
      icon,
      heading,
      parentName,
      live,
      badge,
      searchText: `${name} ${parentName} ${heading} ${urlWords}`.toLowerCase(),
    };
  }

  // ================= API Functions =================
}
